import {
  TILE_DEFAULT_FONT_SIZE_VARIANT,
  TileFontSizeVariant,
  TileGroup,
  TileGroupItem,
  TileVariant,
} from '@/components/diagrams/components/Tile';
import { AfterDeath } from '@/types/schema';
import { formatCurrency } from '@/utils/formatting/currency';

import { useEstateWaterfallContext } from '../contexts/estateWaterfall.context';
import { EstateWaterfall_TaxSummaryV2Fragment } from '../graphql/EstateWaterfall.generated';
import { useTaxBreakout } from '../hooks/useTaxBreakout';

export interface TaxBreakoutProps {
  summary: EstateWaterfall_TaxSummaryV2Fragment;
  nodeId: string;
  variant: TileVariant;
  afterDeath?: AfterDeath;
  firstGrantorDeathId?: string | null;
}

export const TaxBreakout = ({
  summary,
  nodeId,
  variant,
  afterDeath,
  firstGrantorDeathId,
}: TaxBreakoutProps) => {
  const {
    inheritanceTaxesByCode,
    federal,
    showTaxBreakout,
    gstTax,
    displayStateTaxLines,
  } = useTaxBreakout(summary, { afterDeath, firstGrantorDeathId });
  const { state } = useEstateWaterfallContext();
  const existingNode = state.derivedData.nodesById[nodeId];
  const fontSizeVariant =
    (existingNode?.data as { fontSizeVariant?: TileFontSizeVariant })
      ?.fontSizeVariant ?? TILE_DEFAULT_FONT_SIZE_VARIANT;
  if (!showTaxBreakout) return null;

  const federalLabel = 'Federal estate tax';
  const gstLabel = 'GST tax';

  return (
    <TileGroup
      hideItemCount
      tileVariant={variant}
      fontSizeVariant={fontSizeVariant}
    >
      {displayStateTaxLines.map((line) => (
        <TileGroupItem
          key={line.key}
          description={line.label}
          value={formatCurrency(line.tax, { notation: 'compact' })}
          tileVariant={variant}
          fontSizeVariant={fontSizeVariant}
        />
      ))}
      {gstTax?.greaterThan(0) && (
        <TileGroupItem
          description={gstLabel}
          value={formatCurrency(gstTax, { notation: 'compact' })}
          tileVariant={variant}
          fontSizeVariant={fontSizeVariant}
        />
      )}
      {federal.greaterThan(0) && (
        <TileGroupItem
          description={federalLabel}
          value={formatCurrency(federal, {
            notation: 'compact',
          })}
          tileVariant={variant}
          fontSizeVariant={fontSizeVariant}
        />
      )}
      {Object.entries(inheritanceTaxesByCode).map(([stateCode, tax]) =>
        tax.greaterThan(0) ? (
          <TileGroupItem
            key={`${stateCode}-inh`}
            description={`${stateCode} inheritance tax`}
            value={formatCurrency(tax, { notation: 'compact' })}
            tileVariant={variant}
            fontSizeVariant={fontSizeVariant}
          />
        ) : null
      )}
    </TileGroup>
  );
};
